import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import "./BookingList.css";
import ManagerSidebar from "../../../components/Sidebar/ManagerSidebar";

import {
  Download,
  Search,
  Filter,
  Calendar,
  Home,
  Mail,
  User,
  CheckCircle,
  XCircle,
  Trash2,
} from "lucide-react";

import { Pie, Bar } from "react-chartjs-2";

import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
} from "chart.js";

import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement
);

const BASE_URL = "https://ikaze-thouse-backend.vercel.app";

const BookingList = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("All");
  const [search, setSearch] = useState("");

  // ================= FORMAT DATE =================
  const formatDate = (dateString) => {
    if (!dateString) return "-";

    return new Date(dateString).toLocaleDateString("en-CA");
  };

  // ================= FETCH BOOKINGS =================
  const fetchBookings = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/api/v1/bookings`);
      setBookings(res.data);
    } catch (error) {
      console.error("Error fetching bookings:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBookings();
  }, []);

  // ================= AUTO REFRESH =================
  useEffect(() => {
    const interval = setInterval(() => {
      fetchBookings();
    }, 10000);

    return () => clearInterval(interval);
  }, []);

  // ================= DELETE BOOKING =================
  const deleteBooking = async (id) => {
    if (
      !window.confirm(
        "Are you sure you want to permanently delete this booking?"
      )
    )
      return;

    try {
      await axios.delete(
        `${BASE_URL}/api/v1/bookings/delete/${id}`
      );

      setBookings((prev) =>
        prev.filter((b) => b.id !== id)
      );
    } catch (error) {
      alert("Failed to delete booking");
    }
  };

  // ================= UPDATE STATUS =================
  const updateStatus = async (id, status) => {
    try {
      const res = await axios.put(
        `${BASE_URL}/api/v1/bookings/status/${id}`,
        { status }
      );

      setBookings((prev) =>
        prev.map((b) =>
          b.id === id
            ? {
              ...b,
              status: res.data.booking.status,
            }
            : b
        )
      );
    } catch (error) {
      alert("Failed to update status");
    }
  };

  // ================= STATS =================
  const stats = useMemo(() => {
    return {
      total: bookings.length,

      pending: bookings.filter(
        (b) => (b.status || "Pending") === "Pending"
      ).length,

      approved: bookings.filter(
        (b) => b.status === "Approved"
      ).length,

      rejected: bookings.filter(
        (b) => b.status === "Rejected"
      ).length,
    };
  }, [bookings]);

  // ================= FILTER + SEARCH =================
  const filteredBookings = useMemo(() => {
    return bookings.filter((b) => {
      const statusMatch =
        filter === "All" ||
        (b.status || "Pending") === filter;

      const searchMatch =
        b.guest_name
          ?.toLowerCase()
          .includes(search.toLowerCase()) ||
        b.email_address
          ?.toLowerCase()
          .includes(search.toLowerCase()) ||
        b.appartment_unit
          ?.toLowerCase()
          .includes(search.toLowerCase());

      return statusMatch && searchMatch;
    });
  }, [bookings, filter, search]);

  // ================= CHART OPTIONS =================
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,

    plugins: {
      legend: {
        position: "bottom",

        labels: {
          font: {
            family: "Inter, sans-serif",
            weight: 500,
          },

          color: "#475569",
        },
      },
    },
  };

  // ================= PIE DATA =================
  const pieData = {
    labels: ["Pending", "Approved", "Rejected"],

    datasets: [
      {
        data: [
          stats.pending,
          stats.approved,
          stats.rejected,
        ],

        backgroundColor: [
          "#f59e0b",
          "#10b981",
          "#ef4444",
        ],

        borderWidth: 0,
      },
    ],
  };

  // ================= BAR DATA =================
  const barData = {
    labels: [
      "Total",
      "Pending",
      "Approved",
      "Rejected",
    ],

    datasets: [
      {
        label: "Bookings",

        data: [
          stats.total,
          stats.pending,
          stats.approved,
          stats.rejected,
        ],

        backgroundColor: [
          "#3b82f6",
          "#f59e0b",
          "#10b981",
          "#ef4444",
        ],

        borderRadius: 8,
      },
    ],
  };

  // ================= EXPORT PDF =================
  const exportPDF = () => {
    const doc = new jsPDF();

    doc.setFontSize(18);
    doc.text("Booking Report Summary", 14, 18);

    autoTable(doc, {
      startY: 28,

      head: [
        [
          "ID",
          "Guest",
          "Apartment",
          "Email",
          "Arrival",
          "Departure",
          "Status",
        ],
      ],

      body: bookings.map((b) => [
        b.id,
        b.guest_name,
        b.appartment_unit,
        b.email_address,
        formatDate(b.arrival_date),
        formatDate(b.departure_date),
        b.status || "Pending",
      ]),

      headStyles: {
        fillColor: [59, 130, 246],
      },
    });

    doc.save("bookings-report.pdf");
  };

  return (
    <div className="dashboard-container">
      <ManagerSidebar />

      <div className="dashboard-content">

        {/* ================= HEADER ================= */}
        <div className="dashboard-header">
          <div>
            <h2 className="title">
              Booking Dashboard
            </h2>

            <p className="subtitle">
              Manage, monitor and review all
              property reservations
            </p>
          </div>

          <button
            className="export-btn"
            onClick={exportPDF}
          >
            <Download size={18} />
            Export PDF
          </button>
        </div>

        {/* ================= STATS ================= */}
        <div className="stats-grid">

          <div className="stat-card total">
            <span className="stat-label">
              Total Bookings
            </span>

            <span className="stat-value">
              {stats.total}
            </span>
          </div>

          <div className="stat-card pending">
            <span className="stat-label">
              Pending Review
            </span>

            <span className="stat-value">
              {stats.pending}
            </span>
          </div>

          <div className="stat-card approved">
            <span className="stat-label">
              Approved
            </span>

            <span className="stat-value">
              {stats.approved}
            </span>
          </div>

          <div className="stat-card rejected">
            <span className="stat-label">
              Rejected
            </span>

            <span className="stat-value">
              {stats.rejected}
            </span>
          </div>

        </div>

        {/* ================= CHARTS ================= */}
        <div className="charts-section">

          <div className="chart-box">
            <h3>Status Distribution</h3>

            <div className="chart-wrapper">
              <Pie
                data={pieData}
                options={chartOptions}
              />
            </div>
          </div>

          <div className="chart-box">
            <h3>Booking Metrics</h3>

            <div className="chart-wrapper">
              <Bar
                data={barData}
                options={chartOptions}
              />
            </div>
          </div>

        </div>

        {/* ================= FILTERS ================= */}
        <div className="filter-toolbar">

          <div className="search-box-wrapper">
            <Search
              className="search-icon"
              size={18}
            />

            <input
              type="text"
              className="search-input"
              placeholder="Search guest, email, apartment..."
              value={search}
              onChange={(e) =>
                setSearch(e.target.value)
              }
            />
          </div>

          <div className="select-box-wrapper">
            <Filter
              className="filter-icon"
              size={18}
            />

            <select
              value={filter}
              onChange={(e) =>
                setFilter(e.target.value)
              }
            >
              <option value="All">
                All Statuses
              </option>

              <option value="Pending">
                Pending
              </option>

              <option value="Approved">
                Approved
              </option>

              <option value="Rejected">
                Rejected
              </option>
            </select>
          </div>

        </div>

        {/* ================= TABLE ================= */}
        {loading ? (
          <div className="loader-container">
            <div className="spinner"></div>
            <p>Loading bookings...</p>
          </div>
        ) : (
          <div className="table-wrapper">

            <table className="booking-table">

              <thead>
                <tr>
                  <th>ID</th>
                  <th>Guest Info</th>
                  <th>Apartment Unit</th>
                  <th>Duration</th>
                  {/* <th>Status</th> */}
                  <th style={{ textAlign: "center" }}>
                    Actions
                  </th>
                </tr>
              </thead>

              <tbody>

                {filteredBookings.length === 0 ? (
                  <tr>
                    <td
                      colSpan="6"
                      className="no-data-cell"
                    >
                      No matching records found.
                    </td>
                  </tr>
                ) : (
                  filteredBookings.map((b) => (
                    <tr key={b.id}>

                      {/* ID */}
                      <td className="id-cell">
                        #{b.id}
                      </td>

                      {/* GUEST INFO */}
                      <td>
                        <div className="guest-info">

                          <span className="guest-name">
                            <User size={14} />
                            {b.guest_name}
                          </span>

                          <span className="guest-email">
                            <Mail size={12} />
                            {b.email_address}
                          </span>

                        </div>
                      </td>

                      {/* UNIT */}
                      <td className="unit-cell">
                        <Home
                          size={14}
                          style={{
                            marginRight: "6px",
                            color: "#64748b",
                          }}
                        />

                        {b.appartment_unit}
                      </td>

                      {/* DATES */}
                      <td>
                        <div className="date-info">

                          <Calendar
                            size={13}
                            style={{
                              color: "#94a3b8",
                            }}
                          />

                          <span>
                            {formatDate(
                              b.arrival_date
                            )}{" "}
                            →{" "}
                            {formatDate(
                              b.departure_date
                            )}
                          </span>

                        </div>
                      </td>

                      {/* STATUS */}
                      {/* <td>
                        <span
                          className={`status-badge ${String(b.status || "Pending")
                              .trim()
                              .toLowerCase()
                            }`}
                        >
                          {String(b.status || "Pending").trim()}
                        </span>
                      </td> */}

                      {/* ACTIONS */}
                      <td className="actions-cell">

                        <div className="action-buttons">

                          <button
                            className={`btn-approve ${(b.status ||
                                "Pending") ===
                                "Approved"
                                ? "disabled-btn"
                                : ""
                              }`}
                            disabled={
                              (b.status ||
                                "Pending") ===
                              "Approved"
                            }
                            onClick={() =>
                              updateStatus(
                                b.id,
                                "Approved"
                              )
                            }
                          >
                            <CheckCircle size={16} />
                            Approve
                          </button>

                          <button
                            className={`btn-reject ${(b.status ||
                                "Pending") ===
                                "Rejected"
                                ? "disabled-btn"
                                : ""
                              }`}
                            disabled={
                              (b.status ||
                                "Pending") ===
                              "Rejected"
                            }
                            onClick={() =>
                              updateStatus(
                                b.id,
                                "Rejected"
                              )
                            }
                          >
                            <XCircle size={16} />
                            Reject
                          </button>

                          <button
                            className="btn-delete"
                            onClick={() =>
                              deleteBooking(b.id)
                            }
                          >
                            <Trash2 size={16} />
                            Delete
                          </button>

                        </div>

                      </td>

                    </tr>
                  ))
                )}

              </tbody>

            </table>

          </div>
        )}
      </div>
    </div>
  );
};

export default BookingList;